import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, type ChangeSetStatus, type ChangeSetView } from '../../api/client';
import { ChangeSetDetail, SourceChip, StatusChip } from '../../components/changesets';
import { Button, Card, Drawer, Empty, LoadError, Loading, PageHeader } from '../../components/ui';
import { actorLabel, shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

const FILTERS: { key: string; label: string; status?: ChangeSetStatus[] }[] = [
  { key: 'all', label: 'All' },
  { key: 'needs_attention', label: 'Needs attention', status: ['needs_attention'] },
  { key: 'planned', label: 'Waiting for approval', status: ['planned'] },
  { key: 'applied', label: 'Applied', status: ['applied'] },
  { key: 'rolled_back', label: 'Rolled back', status: ['rolled_back'] },
  { key: 'undone', label: 'Undone', status: ['undone'] },
  { key: 'other', label: 'Out of date or discarded', status: ['stale', 'discarded'] }
];

export function AdminActivity() {
  const [params, setParams] = useSearchParams();
  const key = params.get('status') ?? 'all';
  const filter = FILTERS.find((f) => f.key === key) ?? FILTERS[0]!;
  const data = useData(() => api.allChangeSets(filter.status, 300), [filter.key]);
  const [open, setOpen] = useState<ChangeSetView | null>(null);

  return (
    <>
      <PageHeader
        eyebrow="Platform · all tenants"
        title="Activity"
        actions={
          <Button icon="refresh" onClick={data.reload}>
            Refresh
          </Button>
        }
      />
      <div className="content">
        <div className="filters" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={f.key === filter.key} onClick={() => setParams(f.key === 'all' ? {} : { status: f.key })}>
              {f.label}
            </button>
          ))}
        </div>
        <Card>
          {data.error ? (
            <LoadError error={data.error} retry={data.reload} />
          ) : !data.data ? (
            <Loading />
          ) : data.data.changeSets.length === 0 ? (
            <Empty title="Nothing here">{filter.key === 'all' ? 'No change sets have been made yet.' : 'No change sets with this status.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Tenant</th>
                    <th scope="col">Change</th>
                    <th scope="col" className="hide-sm">
                      By
                    </th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.changeSets.map((cs) => (
                    <tr key={cs.id} className="clickable" onClick={() => setOpen(cs)}>
                      <td className="num" style={{ whiteSpace: 'nowrap' }}>
                        {shortWhen(cs.createdAt)}
                      </td>
                      <td className="mono">{cs.tenant}</td>
                      <td>
                        <button type="button" className="link-btn" style={{ color: 'inherit', textAlign: 'left' }} onClick={() => setOpen(cs)}>
                          {cs.title}
                        </button>{' '}
                        <SourceChip cs={cs} />
                      </td>
                      <td className="hide-sm">{actorLabel(cs)}</td>
                      <td>
                        <StatusChip status={cs.status} small />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <Drawer open={!!open} onClose={() => setOpen(null)} title={open?.title ?? ''} subtitle={open?.tenant}>
        {open && (
          <ChangeSetDetail
            cs={open}
            extra={
              <Link to={`/t/${open.tenant}/history`} className="btn secondary small" style={{ alignSelf: 'flex-start' }}>
                Open in the tenant's history
              </Link>
            }
          />
        )}
      </Drawer>
    </>
  );
}
